import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';
import { TranslocoDirective } from '@jsverse/transloco';

import { Icon } from '../../shared/components/icon/icon';

/**
 * One setting inside a settings section card: name + description on the left,
 * the projected control on the right, and an optional "Platform default: …"
 * caption with a reset link when the current value differs from that default.
 * Row separators come from the section's `divide-y` container.
 */
@Component({
  selector: 'app-setting-row',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [Icon, TranslocoDirective],
  host: { class: 'block' },
  template: `
    <div
      *transloco="let t"
      class="flex flex-wrap items-start justify-between gap-x-6 gap-y-4 py-4"
    >
      <div class="min-w-[260px] flex-1">
        <div class="text-sm font-semibold leading-5 text-ink-1">{{ label() }}</div>
        @if (help()) {
          <p class="mt-0.5 text-xs leading-[18px] text-ink-2">{{ help() }}</p>
        }
        @if (defaultLabel()) {
          <div class="mt-1 text-xs text-ink-3">{{ defaultLabel() }}</div>
        }
      </div>
      <div class="flex flex-none items-center gap-4">
        <ng-content />
        @if (defaultLabel()) {
          <!-- Icon-only reset; greyed out while the value already matches the
               platform default. Label lives in the tooltip/aria-label. -->
          <button
            type="button"
            (click)="reset.emit()"
            [disabled]="!showReset()"
            [title]="resetLabel() || t('tenantSettings.resetToDefault')"
            [attr.aria-label]="resetLabel() || t('tenantSettings.resetToDefault')"
            class="flex h-8 w-8 flex-none items-center justify-center rounded-control text-ink-1 enabled:hover:bg-sunken disabled:opacity-30"
          >
            <app-icon name="icon-reset" [size]="18" />
          </button>
        }
      </div>
    </div>
  `,
})
export class SettingRow {
  readonly label = input.required<string>();
  readonly help = input('');
  /** Already-composed caption, e.g. "Platform default: 15 min". Empty hides the line. */
  readonly defaultLabel = input('');
  /** Enables the reset icon (value differs from the platform default). */
  readonly showReset = input(false);
  /** Overrides the reset link text (e.g. "Use tenant default"). */
  readonly resetLabel = input('');
  /** Emitted when the reset link is clicked; the parent restores the default value. */
  readonly reset = output<void>();
}
